import { StewardReportDetail } from '../../services/dtos';

export interface WorkerSummary {
  // Hours totals
  totalRegHours: number;
  totalOtHours: number;
  totalDtHours: number;
  
  // Quantities
  totalShowCallQty: number;
  totalMealPenaltyQty: number;
  
  // Rates (from first detail, should be consistent)
  regRate: number;
  otRate: number;
  dtRate: number;
  showCallRate: number;
  mealPenaltyRate: number;
  
  // Wage calculations
  regWage: number; // reg hours * reg rate
  otWage: number; // ot hours * ot rate
  dtWage: number; // dt hours * dt rate
  showCallWage: number; // show qty * show rate
  mealPenaltyWage: number; // meal qty * meal rate
  subtotal: number; // sum of all wages
  vacation: number; // 8% of subtotal
  totalWage: number; // subtotal + vacation
  unionFee: number; // 3.5% of total wage
}

/**
 * Calculate per-worker summary from detail records
 */
export function calculateWorkerSummary(details: StewardReportDetail[]): WorkerSummary {
  if (details.length === 0) {
    return {
      totalRegHours: 0,
      totalOtHours: 0,
      totalDtHours: 0,
      totalShowCallQty: 0,
      totalMealPenaltyQty: 0,
      regRate: 0,
      otRate: 0,
      dtRate: 0,
      showCallRate: 0,
      mealPenaltyRate: 0,
      regWage: 0,
      otWage: 0,
      dtWage: 0,
      showCallWage: 0,
      mealPenaltyWage: 0,
      subtotal: 0,
      vacation: 0,
      totalWage: 0,
      unionFee: 0,
    };
  }

  // Get rates from first detail (should be consistent across all days)
  const firstDetail = details[0];
  const regRate = firstDetail.regTimeRate || 0;
  const otRate = firstDetail.regTimeRate ? firstDetail.regTimeRate * 1.5 : 0; // Default to 1.5x if not set
  const dtRate = firstDetail.regTimeRate ? firstDetail.regTimeRate * 2 : 0; // Default to 2x if not set
  const showCallRate = firstDetail.showCallRate || 0;
  const mealPenaltyRate = firstDetail.mealPenaltyRate || 0;

  // Sum up hours and quantities across all days
  let totalRegHours = 0;
  let totalOtHours = 0;
  let totalDtHours = 0;
  let totalShowCallQty = 0;
  let totalMealPenaltyQty = 0;

  details.forEach((detail) => {
    totalRegHours += detail.regTimeHours || 0;
    totalOtHours += detail.overTimeHours || 0;
    totalDtHours += detail.doubleTimeHours || 0;
    totalShowCallQty += detail.showCallQty || 0;
    totalMealPenaltyQty += detail.mealPenaltyQty || 0;
  });

  // Calculate wages
  const regWage = totalRegHours * regRate;
  const otWage = totalOtHours * otRate;
  const dtWage = totalDtHours * dtRate;
  const showCallWage = totalShowCallQty * showCallRate;
  const mealPenaltyWage = totalMealPenaltyQty * mealPenaltyRate;

  // Calculate subtotal
  const subtotal = regWage + otWage + dtWage + showCallWage + mealPenaltyWage;

  // Calculate vacation (8% of subtotal)
  const vacation = subtotal * 0.08;

  // Calculate total wage (subtotal + vacation)
  const totalWage = subtotal + vacation;

  // Calculate union fee (3.5% of total wage)
  const unionFee = totalWage * 0.035;

  return {
    totalRegHours,
    totalOtHours,
    totalDtHours,
    totalShowCallQty,
    totalMealPenaltyQty,
    regRate,
    otRate,
    dtRate,
    showCallRate,
    mealPenaltyRate,
    regWage,
    otWage,
    dtWage,
    showCallWage,
    mealPenaltyWage,
    subtotal,
    vacation,
    totalWage,
    unionFee,
  };
}

export interface ReportSummary {
  workerCount: number;
  totalRegHours: number;
  totalOtHours: number;
  totalDtHours: number;
  totalShowCallQty: number;
  totalMealPenaltyQty: number;
  totalRegWage: number;
  totalOtWage: number;
  totalDtWage: number;
  totalShowCallWage: number;
  totalMealPenaltyWage: number;
  totalSubtotal: number;
  totalVacation: number;
  totalWage: number;
  totalUnionFee: number;
  grossWageTotal: number;
  healthAndWelfare: number;
  pension: number;
  workFee: number;
  training: number;
  totalAddOns: number;
  grandTotal: number;
}

/**
 * Calculate report-level summary from all detail records
 */
export function calculateReportSummary(details: StewardReportDetail[]): ReportSummary {
  // Group details by uniqueMemberId
  const workerGroups = new Map<string, StewardReportDetail[]>();
  details.forEach((detail) => {
    if (!workerGroups.has(detail.uniqueMemberId)) {
      workerGroups.set(detail.uniqueMemberId, []);
    }
    workerGroups.get(detail.uniqueMemberId)!.push(detail);
  });

  // Calculate summary for each worker and aggregate
  let totalRegHours = 0;
  let totalOtHours = 0;
  let totalDtHours = 0;
  let totalShowCallQty = 0;
  let totalMealPenaltyQty = 0;
  let totalRegWage = 0;
  let totalOtWage = 0;
  let totalDtWage = 0;
  let totalShowCallWage = 0;
  let totalMealPenaltyWage = 0;

  workerGroups.forEach((workerDetails) => {
    const workerSummary = calculateWorkerSummary(workerDetails);
    totalRegHours += workerSummary.totalRegHours;
    totalOtHours += workerSummary.totalOtHours;
    totalDtHours += workerSummary.totalDtHours;
    totalShowCallQty += workerSummary.totalShowCallQty;
    totalMealPenaltyQty += workerSummary.totalMealPenaltyQty;
    totalRegWage += workerSummary.regWage;
    totalOtWage += workerSummary.otWage;
    totalDtWage += workerSummary.dtWage;
    totalShowCallWage += workerSummary.showCallWage;
    totalMealPenaltyWage += workerSummary.mealPenaltyWage;
  });

  const totalSubtotal = totalRegWage + totalOtWage + totalDtWage + totalShowCallWage + totalMealPenaltyWage;
  const totalVacation = totalSubtotal * 0.08;
  const totalWage = totalSubtotal + totalVacation;
  const totalUnionFee = totalWage * 0.035;

  // Report-level add-ons
  const grossWageTotal = totalWage;
  const healthAndWelfare = grossWageTotal * 0.155;
  const pension = grossWageTotal * 0.104;
  const workFee = grossWageTotal * 0.035;
  const training = grossWageTotal * 0.005;
  const totalAddOns = healthAndWelfare + pension + workFee + training;
  const grandTotal = grossWageTotal + totalAddOns;

  return {
    workerCount: workerGroups.size,
    totalRegHours,
    totalOtHours,
    totalDtHours,
    totalShowCallQty,
    totalMealPenaltyQty,
    totalRegWage,
    totalOtWage,
    totalDtWage,
    totalShowCallWage,
    totalMealPenaltyWage,
    totalSubtotal,
    totalVacation,
    totalWage,
    totalUnionFee,
    grossWageTotal,
    healthAndWelfare,
    pension,
    workFee,
    training,
    totalAddOns,
    grandTotal,
  };
}

/**
 * Calculate report-level summary from local state (unsaved changes)
 */
export function calculateReportSummaryFromLocalState(
  workerLocalStates: Map<string, { localValues: Map<string, string>; rates: { regTimeRate: number; showCallRate: number; mealPenaltyRate: number } }>,
  weekDates: Date[],
  details: StewardReportDetail[]
): ReportSummary {
  // Helper to normalize dateFor to just the date portion (YYYY-MM-DD)
  const normalizeDateFor = (dateFor: string | undefined): string => {
    if (!dateFor) return '';
    if (dateFor.length === 10 && dateFor.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateFor;
    }
    return dateFor.split(' ')[0].split('T')[0];
  };

  // Group details by uniqueMemberId to get worker count
  const workerGroups = new Map<string, StewardReportDetail[]>();
  details.forEach((detail) => {
    if (!workerGroups.has(detail.uniqueMemberId)) {
      workerGroups.set(detail.uniqueMemberId, []);
    }
    workerGroups.get(detail.uniqueMemberId)!.push(detail);
  });

  // Add workers that have local state but no details yet
  workerLocalStates.forEach((_, uniqueMemberId) => {
    if (!workerGroups.has(uniqueMemberId)) {
      workerGroups.set(uniqueMemberId, []);
    }
  });

  let totalRegHours = 0;
  let totalOtHours = 0;
  let totalDtHours = 0;
  let totalShowCallQty = 0;
  let totalMealPenaltyQty = 0;
  let totalRegWage = 0;
  let totalOtWage = 0;
  let totalDtWage = 0;
  let totalShowCallWage = 0;
  let totalMealPenaltyWage = 0;

  workerLocalStates.forEach((localState, uniqueMemberId) => {
    const { localValues, rates } = localState;
    const regRate = rates.regTimeRate || 0;
    const otRate = regRate * 1.5;
    const dtRate = regRate * 2;
    const showCallRate = rates.showCallRate || 0;
    const mealPenaltyRate = rates.mealPenaltyRate || 0;

    let workerRegHours = 0;
    let workerOtHours = 0;
    let workerDtHours = 0;
    let workerShowCallQty = 0;
    let workerMealPenaltyQty = 0;

    // Sum up values from local state for all week dates
    weekDates.forEach((date) => {
      const dateStr = date.toISOString().split('T')[0];
      
      // Get values from local state, fallback to detail if not in local state
      const regKey = `${dateStr}-reg`;
      const otKey = `${dateStr}-ot`;
      const dtKey = `${dateStr}-dt`;
      const showKey = `${dateStr}-show`;
      const mealKey = `${dateStr}-meal`;

      // Find matching detail for this date
      const detail = details.find(
        (d) => normalizeDateFor(d.dateFor) === dateStr && d.uniqueMemberId === uniqueMemberId
      );

      // Get reg hours
      if (localValues.has(regKey)) {
        workerRegHours += parseFloat(localValues.get(regKey)!) || 0;
      } else if (detail) {
        workerRegHours += detail.regTimeHours || 0;
      }

      // Get OT hours
      if (localValues.has(otKey)) {
        workerOtHours += parseFloat(localValues.get(otKey)!) || 0;
      } else if (detail) {
        workerOtHours += detail.overTimeHours || 0;
      }

      // Get DT hours
      if (localValues.has(dtKey)) {
        workerDtHours += parseFloat(localValues.get(dtKey)!) || 0;
      } else if (detail) {
        workerDtHours += detail.doubleTimeHours || 0;
      }

      // Get show call qty
      if (localValues.has(showKey)) {
        workerShowCallQty += parseInt(localValues.get(showKey)!) || 0;
      } else if (detail) {
        workerShowCallQty += detail.showCallQty || 0;
      }

      // Get meal penalty qty
      if (localValues.has(mealKey)) {
        workerMealPenaltyQty += parseInt(localValues.get(mealKey)!) || 0;
      } else if (detail) {
        workerMealPenaltyQty += detail.mealPenaltyQty || 0;
      }
    });

    // Calculate wages for this worker
    const regWage = workerRegHours * regRate;
    const otWage = workerOtHours * otRate;
    const dtWage = workerDtHours * dtRate;
    const showCallWage = workerShowCallQty * showCallRate;
    const mealPenaltyWage = workerMealPenaltyQty * mealPenaltyRate;

    // Add to totals
    totalRegHours += workerRegHours;
    totalOtHours += workerOtHours;
    totalDtHours += workerDtHours;
    totalShowCallQty += workerShowCallQty;
    totalMealPenaltyQty += workerMealPenaltyQty;
    totalRegWage += regWage;
    totalOtWage += otWage;
    totalDtWage += dtWage;
    totalShowCallWage += showCallWage;
    totalMealPenaltyWage += mealPenaltyWage;
  });

  // Also include workers that don't have local state (use details only)
  workerGroups.forEach((workerDetails, uniqueMemberId) => {
    if (!workerLocalStates.has(uniqueMemberId) && workerDetails.length > 0) {
      const workerSummary = calculateWorkerSummary(workerDetails);
      totalRegHours += workerSummary.totalRegHours;
      totalOtHours += workerSummary.totalOtHours;
      totalDtHours += workerSummary.totalDtHours;
      totalShowCallQty += workerSummary.totalShowCallQty;
      totalMealPenaltyQty += workerSummary.totalMealPenaltyQty;
      totalRegWage += workerSummary.regWage;
      totalOtWage += workerSummary.otWage;
      totalDtWage += workerSummary.dtWage;
      totalShowCallWage += workerSummary.showCallWage;
      totalMealPenaltyWage += workerSummary.mealPenaltyWage;
    }
  });

  const totalSubtotal = totalRegWage + totalOtWage + totalDtWage + totalShowCallWage + totalMealPenaltyWage;
  const totalVacation = totalSubtotal * 0.08;
  const totalWage = totalSubtotal + totalVacation;
  const totalUnionFee = totalWage * 0.035;

  // Report-level add-ons
  const grossWageTotal = totalWage;
  const healthAndWelfare = grossWageTotal * 0.155;
  const pension = grossWageTotal * 0.104;
  const workFee = grossWageTotal * 0.035;
  const training = grossWageTotal * 0.005;
  const totalAddOns = healthAndWelfare + pension + workFee + training;
  const grandTotal = grossWageTotal + totalAddOns;

  return {
    workerCount: workerGroups.size,
    totalRegHours,
    totalOtHours,
    totalDtHours,
    totalShowCallQty,
    totalMealPenaltyQty,
    totalRegWage,
    totalOtWage,
    totalDtWage,
    totalShowCallWage,
    totalMealPenaltyWage,
    totalSubtotal,
    totalVacation,
    totalWage,
    totalUnionFee,
    grossWageTotal,
    healthAndWelfare,
    pension,
    workFee,
    training,
    totalAddOns,
    grandTotal,
  };
}

/**
 * Format currency value
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format hours value
 */
export function formatHours(value: number): string {
  return value.toFixed(2);
}

